<template>
  <div id="subscription-type-create">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>
    <v-card elevation="8" class="rounded-xl">
      <!-- Header Section with Gradient -->
      <v-card-title
        class="page-header-gradient white--text d-flex align-center pa-5"
      >
        <v-icon left large color="white" class="mr-3">mdi-credit-card</v-icon>
        <h2 class="text-h5 font-weight-bold">{{ pageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closePrompt" large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Loading Indicator -->
        <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <v-container class="py-0">
            <!-- Required Field Note -->
            <v-row>
              <v-col cols="12">
                <v-alert type="info" dense text outlined class="mb-4">
                  <small
                    ><span class="text-danger">*</span> indicates required
                    field</small
                  >
                </v-alert>
              </v-col>
            </v-row>
            <!-- Year, Contribution Type, Member Type Row -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Year
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="JciYearCodeOptions"
                  :rules="JciYearCodeRules"
                  :loading="JciYearCodeOptionsLoading"
                  v-model="JciYearCode"
                  placeholder="Select Year"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-calendar"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Contribution Type
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="ContributionTypeIdOptions"
                  :rules="ContributionTypeIdRules"
                  :loading="ContributionTypeIdOptionsLoading"
                  v-model="ContributionTypeId"
                  placeholder="Select Contribution Type"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-folder-multiple"
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">Member Type</h6>
                <v-card class="pa-3 elevation-1">
                  <p class="mb-0 font-weight-bold">{{ MemberTypeName }}</p>
                </v-card>
              </v-col>
            </v-row>

            <!-- Start Date, End Date, Voting Permission -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Start Date
                </h6>
                <v-menu
                  v-model="menu1"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="StartDate"
                      placeholder="Select Start Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="StartDate"
                    @input="menu1 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> End Date
                </h6>
                <v-menu
                  v-model="menu2"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="EndDate"
                      placeholder="Select End Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="EndDate"
                    @input="menu2 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Voting Permission
                </h6>
                <v-switch
                  v-model="VotingFlag"
                  :label="`${VotingFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Amount, Tax Percentage, Final Amount -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Amount
                </h6>
                <v-text-field
                  v-model="Amount"
                  :rules="AmountRules"
                  placeholder="Enter Amount"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-inr"
                  @input="calculateTotal()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Tax Percentage
                </h6>
                <v-text-field
                  v-model="TaxPercentage"
                  :rules="TaxPercentageRules"
                  placeholder="Enter Tax Percentage"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-percent"
                  @input="calculateTotal()"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Final Amount
                </h6>
                <v-card class="pa-3 elevation-1">
                  <p class="mb-0">
                    <v-icon small class="mr-2">mdi-calculator</v-icon>
                    <span class="font-weight-bold text-h6">
                      {{ FinalAmount }}</span
                    >
                  </p>
                </v-card>
              </v-col>
            </v-row>

            <!-- No. of Times, Fine, Fine Flag -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> No. of Times
                </h6>
                <v-text-field
                  v-model="NoOfTimes"
                  :rules="NoOfTimesRules"
                  placeholder="Enter Number of Times"
                  type="number"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-counter"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Balance Amount
                </h6>
                <v-text-field
                  v-model="BalanceAmount"
                  :rules="BalanceAmountRules"
                  placeholder="Enter Balance Amount"
                  type="text"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-inr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Penalty
                </h6>
                <v-switch
                  v-model="FineFlag"
                  :label="`${FineFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Is Payment Allowed After
                  Penalty Deadline?
                </h6>
                <v-switch
                  v-model="DeadlineFlag"
                  :label="`${DeadlineFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Fine Amount and Fine End Date (Conditional) -->
            <v-row v-if="FineFlag">
              <v-col cols="12" sm="6" md="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Fine Amount
                </h6>
                <v-text-field
                  v-model="FineAmount"
                  :rules="FineAmountRules"
                  placeholder="Enter Fine Amount"
                  type="text"
                  required
                  dense
                  outlined
                  prepend-inner-icon="mdi-currency-inr"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Penalty End Date
                </h6>
                <v-menu
                  v-model="menu3"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="FineEndDate"
                      placeholder="Select Penalty End Date"
                      readonly
                      outlined
                      dense
                      prepend-inner-icon="mdi-calendar-alert"
                      v-bind="attrs"
                      v-on="on"
                      required
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="FineEndDate"
                    @input="menu3 = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <!-- Advance Payment -->
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Advance Payment
                </h6>
                <v-switch
                  v-model="PaymentFlag"
                  :label="`${PaymentFlag ? 'Yes' : 'No'}`"
                  class="mt-0"
                ></v-switch>
              </v-col>
            </v-row>
          </v-container>

          <!-- Action Buttons -->
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="!valid1"
                @click.prevent="confirmAlert"
                :loading="SubmitFlag"
                elevation="12"
                rounded
                large
                color="#1db954"
                class="font-size-h6 px-8 mr-3 white--text"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Save
              </v-btn>
              <v-btn
                @click="closePrompt"
                elevation="12"
                rounded
                large
                color="#757575"
                class="font-size-h6 px-8 white--text"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
    },
    pageDescription: {
      type: String,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      OverlayLoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,

      rows: [],

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      ContributionTypeIdRules: [(v) => !!v || "Contribution Type is required"],
      ContributionTypeId: "",
      ContributionTypeIdOptions: [],
      ContributionTypeIdOptionsLoading: false,

      MemberTypeName: "",

      VotingFlag: 0,
      VotingFlagRules: [(v) => !!v || "Voting Permission is required"],

      StartDate: "",
      menu1: false,

      EndDate: "",
      menu2: false,

      Amount: "",
      AmountRules: [(v) => !!v || "Amount is required"],

      TaxPercentage: "",
      TaxPercentageRules: [(v) => !!v || "Tax Percentage is required"],

      FinalAmount: 0,

      FineFlag: 0,
      FineFlagRules: [(v) => !!v || "Fine is required"],

      DeadlineFlag: 0,
      DeadlineFlagRules: [(v) => !!v || "Is allow deadline is required"],

      PaymentFlag: 0,
      PaymentFlagRules: [(v) => !!v || "Is allow payment is required"],

      FineAmount: "",
      FineAmountRules: [(v) => !!v || "Fine Amount is required"],

      BalanceAmount: "",
      BalanceAmountRules: [(v) => !!v || "Balance Amount is required"],
      
      FineEndDate: "",
      menu3: false,

      NoOfTimes: "",
      NoOfTimesRules: [(v) => !!v || "No of times is required"],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var ZoneCode = this.$session.get("ZoneId");
        ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
        console.log("ZoneCode=" + ZoneCode);
        this.ZoneCode = ZoneCode;

        this.pageData();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.JciYearCodeOptionsLoading = false;
    },
    ContributionTypeIdOptions: function () {
      console.log("watch ContributionTypeIdOptions");
      this.ContributionTypeIdOptionsLoading = false;
    },
    ContributionTypeId: function () {
      console.log("watch ContributionTypeId");
      var searchValue1 = this.ContributionTypeId;
      var index1 = this.ContributionTypeIdOptions.map((e) => e.value).indexOf(
        searchValue1
      );
      console.log("searchValue1=" + searchValue1 + ", index1=" + index1);
      var MemberTypeName =
        this.ContributionTypeIdOptions[index1].MemberTypeName;
      console.log("MemberTypeName=" + MemberTypeName);
      this.MemberTypeName = MemberTypeName;
    },
  },
  created() {
    // this.initialize()
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "membership_subscriptions",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.LoadingFlag = false;
    },
    pageData() {
      console.log("pageData called");
      this.getJciYearCodeOptions();
      this.getContributionTypeIdOptions();
    },
    validateForm() {
      console.log("validateForm called");
      this.$refs.form1.validate();
    },
    resetForm() {
      console.log("resetForm called");
      this.$refs.form1.reset();
      //   this.$refs.ProfilePicture.reset();
    },
    resetValidation() {
      console.log("resetValidation called");
      this.$refs.form1.resetValidation();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideAddRecordPrompt", this.ResultFlag);
    },
    getJciYearCodeOptions() {
      this.JciYearCodeOptionsLoading = true;
      console.log("getJciYearCodeOptions called");
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getContributionTypeIdOptions() {
      this.ContributionTypeIdOptionsLoading = true;
      console.log("ContributionTypeIdOptions called");
      var selectbox1_source = "ContributionTypeId";
      var selectbox1_destination = "ContributionTypeIdOptions";
      var selectbox1_url = "api/contribution-type/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    calculateTotal() {
      console.log("calculateTotal called");
      var Amount = this.Amount;
      var TaxPercentage = this.TaxPercentage;
      Amount = !isNaN(Amount) ? (Amount >= 0 ? Amount : 0) : 0;
      TaxPercentage =
        !isNaN(TaxPercentage) && TaxPercentage >= 0 ? TaxPercentage : 0;
      console.log("Amount=" + Amount + ", TaxPercentage=" + TaxPercentage);
      var TaxAmount = parseFloat(Amount) * (parseFloat(TaxPercentage) / 100);
      console.log("TaxAmount=" + TaxAmount);
      var TotalAmount = parseFloat(Amount) + parseFloat(TaxAmount);
      console.log("TotalAmount=" + TotalAmount);
      this.FinalAmount = isNaN(TotalAmount) ? 0 : TotalAmount;
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        this.toast("error", "Kindly fill the required fields", true);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);

      //   validate1 = false;

      if (validate1) {
        this.SubmitFlag = true;
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-contribution/store";
        var upload = {
          YearId: this.JciYearCode,
          ContributionTypeId: this.ContributionTypeId,
          StartDate: this.StartDate,
          EndDate: this.EndDate,
          Amount: this.Amount,
          TaxPercentage: this.TaxPercentage,
          FineFlag: this.FineFlag,
          DeadlineFlag: this.DeadlineFlag,
          PaymentFlag: this.PaymentFlag,
          FineAmount: this.FineAmount,
          FineEndDate: this.FineEndDate,
          VotingFlag: this.VotingFlag,
          NoOfTimes: this.NoOfTimes,
          BalanceAmount: this.BalanceAmount,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.ResultFlag = true;
              thisIns.resetForm();
              thisIns.closePrompt();
              thisIns.sweetAlert("success", output, true);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            this.OverlayLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields";
        }
        thisIns.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-header-gradient {
  background: linear-gradient(90deg, #004e92, #0a79df);
  border-radius: 10px 10px 0 0;
}

.v-card {
  border-radius: 12px;
  max-height: 90vh;
  overflow-y: auto;
}

/* Custom scrollbar styling */
.v-card::-webkit-scrollbar {
  width: 8px;
}

.v-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.text-danger {
  color: #f44336;
  font-weight: bold;
}

h6 {
  font-size: 14px;
  letter-spacing: 0.5px;
  color: #333;
}

.rounded-xl {
  border-radius: 12px;
}
</style>
